import {
	ChangeDetectionStrategy,
	Component,
	input,
	InputSignal,
	OnChanges,
	OnDestroy,
	OnInit,
	signal,
	SimpleChanges,
	WritableSignal,
	inject,
} from '@angular/core'
import { PositionsInterface } from '@modules/positions-module/interfaces/positions.interface'
import { Subject, takeUntil } from 'rxjs'
import { WidgetInterface } from '@modules/widgets-module/interfaces/widget.interface'
import { PositionColumnsEnum } from '@shared/components/trading/widgets/positions-widget/core/enums/position-columns.enum'
import { PositionSettingsInterface } from '@shared/components/trading/widgets/positions-widget/core/interfaces/position-settings.interface'
import { ColumnInterface } from '@shared/interfaces/column.interface'
import { TradingLauncherService } from '@shared/services/trading-launcher.service'
import { OrderStateEnum } from '@modules/orders-module/enums/order-state.enum'
import { OrderTypeEnum } from '@modules/orders-module/enums/order-type.enum'
import { OrderSideEnum } from '@modules/orders-module/enums/order-side.enum'
import { UseLocales } from '@modules/lang-module/decorators/use-locales'
import ru from './core/locale/ru.json'
import en from './core/locale/en.json'
import { PricePipe } from '@shared/pipes/price.pipe'
import { QuantityPipe } from '@shared/pipes/quantity.pipe'
import { HighlightDirective } from '@shared/directives/higlight.directive'
import { ProfitPipe } from '@shared/pipes/profit.pipe'
import { PositionStocksActionsComponent } from '@shared/components/trading/positions/stocks/common/position-stocks-actions/position-stocks-actions.component'
import { PercentPipe } from '@shared/pipes/percent.pipe'
import { TableColumnComponent } from '@shared/components/common/table/core/components/table-column/table-column.component'
import { TableActionsComponent } from '@shared/components/common/table/core/components/table-actions/table-actions.component'
import { PositionsStocksHelperService } from '@modules/stocks-module/positions/services/positions-stocks-helper.service'
import { LiveTickerService } from '@modules/stocks-module/live-ticker/services/live-ticker.service'
import { StocksOrdersHelperService } from '@modules/stocks-module/orders/services/stocks-orders-helper.service'
import { OrderInterface } from '@modules/orders-module/interfaces/order.interface'
import { WidgetsService } from '@modules/widgets-module/services/widgets.service'
import { UserHelperService } from '@modules/user-module/services/user-helper.service'
import { SecretDirective } from '@shared/directives/secret.directive'
import { TableFooterComponent } from '@shared/components/common/table/core/components/table-footer/table-footer.component'
import { TranslatePipe } from '@ngx-translate/core'
import { TuiSkeleton } from '@taiga-ui/kit'
import { ColumnSortInterface } from '@shared/components/common/table/core/interfaces/column-sort.interface'
import { TableComponent } from '@shared/components/common/table/table.component'
import { AssetTypesComponent } from '@shared/components/common/asset-type/asset-type.component'

@UseLocales({ ru, en })
@Component({
	selector: 'app-positions-stocks',
	templateUrl: './positions-stocks.component.html',
	styleUrl: './positions-stocks.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		TableColumnComponent,
		PricePipe,
		QuantityPipe,
		HighlightDirective,
		ProfitPipe,
		TableActionsComponent,
		PositionStocksActionsComponent,
		PercentPipe,
		SecretDirective,
		TableFooterComponent,
		TranslatePipe,
		TuiSkeleton,
		TableComponent,
		AssetTypesComponent,
	],
})
export class PositionsStocksComponent implements OnChanges, OnInit, OnDestroy {
	protected readonly number: NumberConstructor = Number
	protected readonly math: Math = Math
	protected readonly orderStateEnum: typeof OrderStateEnum = OrderStateEnum
	protected readonly orderTypeEnum: typeof OrderTypeEnum = OrderTypeEnum
	protected readonly orderSideEnum: typeof OrderSideEnum = OrderSideEnum
	protected readonly positionColumnsEnum: typeof PositionColumnsEnum = PositionColumnsEnum

	public widget: InputSignal<WidgetInterface<PositionSettingsInterface> | null> =
		input<WidgetInterface<PositionSettingsInterface> | null>(null)

	private _destroy$: Subject<void> = new Subject<void>()

	private readonly _tradingLauncherService: TradingLauncherService =
		inject<TradingLauncherService>(TradingLauncherService)
	private readonly _positionsStocksHelperService: PositionsStocksHelperService =
		inject<PositionsStocksHelperService>(PositionsStocksHelperService)
	private readonly _liveTickerService: LiveTickerService = inject<LiveTickerService>(LiveTickerService)
	private readonly _ordersStocksHelperService: StocksOrdersHelperService =
		inject<StocksOrdersHelperService>(StocksOrdersHelperService)
	private readonly _widgetsService: WidgetsService = inject<WidgetsService>(WidgetsService)
	private readonly _userHelperService: UserHelperService = inject<UserHelperService>(UserHelperService)

	protected positions: WritableSignal<PositionsInterface[]> = signal<PositionsInterface[]>([])
	protected isLoadingPositions: WritableSignal<boolean> = signal(false)
	protected statesColumns: WritableSignal<ColumnInterface<PositionColumnsEnum>[]> = signal([])
	protected isSecret: WritableSignal<boolean> = signal<boolean>(false)
	private _listenSecret(): void {
		this._userHelperService.isSecret$.pipe(takeUntil(this._destroy$)).subscribe((value: boolean): void => {
			this.isSecret.set(value)
		})
	}

	private _initPositionsParams(): void {
		this._positionsStocksHelperService.positionsParams$.next({
			page: 1,
			pageSize: 100,
		})
	}

	private _launchPositions(): void {
		this._tradingLauncherService
			.launchPositions$()
			.pipe(takeUntil(this._destroy$))
			.subscribe(([positions, loading]: [PositionsInterface[], boolean]): void => {
				this.positions.set(positions)
				this.isLoadingPositions.set(loading)
			})
	}

	private _launchOpenedOrders(): void {
		this._tradingLauncherService.launchOrders$(this.orderStateEnum.OPEN).pipe(takeUntil(this._destroy$)).subscribe()
	}

	private _updateCurrentColumns(): void {
		const widget: WidgetInterface<PositionSettingsInterface> | null = this.widget()
		if (!widget) return

		this.statesColumns.set([...widget.settings.stocks.columns])
	}

	private _checkHasSlTp(position: PositionsInterface, type: 'stopLoss' | 'takeProfit'): boolean {
		const orders: OrderInterface[] =
			this._ordersStocksHelperService.ordersResponseMap.get(this.orderStateEnum.OPEN)?.getValue()?.orders ?? []

		return orders.some((order: OrderInterface): boolean => {
			if (order.symbol !== position.symbol) return false

			if (order.children_order?.length) {
				const slTpOrder: OrderInterface | null =
					type === 'stopLoss'
						? this._ordersStocksHelperService.findStopLoss(order)
						: this._ordersStocksHelperService.findTakeProfit(order)

				if (slTpOrder && this._ordersStocksHelperService.isSlTpActive(slTpOrder)) return true
			}

			if (!this._ordersStocksHelperService.isSlTpActive(order)) return false
			if (type === 'stopLoss') {
				if (order.type === this.orderTypeEnum.STOP || order.type === this.orderTypeEnum.STOP_LIMIT) return true
				if (order.type === this.orderTypeEnum.TRAILING_STOP) {
					return (
						(position.qty > 0 && order.side === this.orderSideEnum.SELL) ||
						(position.qty < 0 && order.side === this.orderSideEnum.BUY)
					)
				}
				return false
			}

			return order.type === this.orderTypeEnum.LIMIT
		})
	}

	protected checkHasStopLoss(positions: PositionsInterface): boolean {
		return this._checkHasSlTp(positions, 'stopLoss')
	}

	protected checkHasTakeProfit(positions: PositionsInterface): boolean {
		return this._checkHasSlTp(positions, 'takeProfit')
	}

	protected setLiveTicker(position: PositionsInterface): void {
		this._widgetsService.setLiveTickerToDashboard(position.symbol)
		this._liveTickerService.launchLiveTicker(position.symbol)
	}

	protected calcProfitabilityDollar(position: PositionsInterface, isPerDay: boolean = false): number {
		const profitability: number = isPerDay ? +position.unrealized_intraday_pl : +position.unrealized_pl
		return +profitability.toFixed(2)
	}

	protected calcProfitabilityPercent(position: PositionsInterface, isPerDay: boolean = false): number {
		const profitability: number = isPerDay ? +position.unrealized_intraday_plpc : +position.unrealized_plpc
		return +(profitability * 100).toFixed(2)
	}

	protected changeSort(activeSort: ColumnSortInterface<keyof PositionsInterface>): void {
		const widget: WidgetInterface<PositionSettingsInterface> | null = this.widget()
		if (!widget) return

		this._widgetsService.updateWidget({
			...widget,
			settings: {
				...widget.settings,
				stocks: {
					...widget.settings.stocks,
					activeSort: activeSort,
				},
			},
		})
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['widget']) {
			this._updateCurrentColumns()
		}
	}

	ngOnInit(): void {
		this._listenSecret()
		this._initPositionsParams()
		this._launchPositions()
		this._launchOpenedOrders()
	}

	ngOnDestroy(): void {
		this._destroy$.next()
		this._destroy$.complete()
	}
}
